/**
 * Preset upgrade detection — structural vs non-structural changes.
 *
 * Non-structural changes (config values, placeholder defaults) propagate
 * automatically per pin policy. Structural changes (added/removed items,
 * changed refs) require explicit `skaile preset upgrade`.
 *
 * The upgrade detector compares two preset versions and classifies the diff.
 *
 * @docLink packages/library/concepts#upgrade-strategies
 */

import type { PresetItem, PresetManifest } from "@skaile/workspaces/types/manifests";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Classification of the diff between two preset versions.
 *
 * @docLink packages/library/concepts#upgrade-strategies
 */
export type ChangeKind = "structural" | "non-structural";

/**
 * Detailed diff between two preset manifest versions.
 *
 * @docLink packages/library/concepts#upgrade-strategies
 */
export interface PresetDiff {
  /** Overall classification — `structural` or `non-structural`. */
  kind: ChangeKind;
  /** Items added in the new version (structural). */
  addedItems: PresetItem[];
  /** Items removed from the old version (structural). */
  removedItems: PresetItem[];
  /** Items with changed refs or placeholder/materialize changes (structural). */
  changedRefItems: Array<{ old: PresetItem; new: PresetItem }>;
  /** Items with config-only changes (non-structural, safe to auto-propagate). */
  configChangedItems: Array<{ old: PresetItem; new: PresetItem }>;
  /** Human-readable summary string for CLI display. */
  summary: string;
}

/**
 * Result of {@link evaluateUpgrade} — indicates whether the upgrade can proceed automatically.
 *
 * @docLink packages/library/concepts#upgrade-strategies
 */
export interface UpgradeResult {
  /** Whether upgrade can proceed without user confirmation. */
  autoUpgradable: boolean;
  /** The computed diff between old and new. */
  diff: PresetDiff;
  /** If structural, the list of actions that require explicit user approval. */
  requiredActions?: string[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Detect changes between two preset versions.
 *
 * @param oldManifest - The currently applied preset manifest.
 * @param newManifest - The new preset manifest to upgrade to.
 * @returns Classified diff with added/removed/changed item lists.
 * @docLink packages/library/concepts#upgrade-strategies
 */
export function detectChanges(
  oldManifest: PresetManifest,
  newManifest: PresetManifest,
): PresetDiff {
  const oldItems = indexItems(oldManifest.items);
  const newItems = indexItems(newManifest.items);

  const addedItems: PresetItem[] = [];
  const removedItems: PresetItem[] = [];
  const changedRefItems: Array<{ old: PresetItem; new: PresetItem }> = [];
  const configChangedItems: Array<{ old: PresetItem; new: PresetItem }> = [];

  // Find removed items (in old, not in new)
  for (const [key, oldItem] of oldItems) {
    if (!newItems.has(key)) {
      removedItems.push(oldItem);
    }
  }

  // Find added items (in new, not in old)
  for (const [key, newItem] of newItems) {
    if (!oldItems.has(key)) {
      addedItems.push(newItem);
    }
  }

  // Find changed items (in both)
  for (const [key, newItem] of newItems) {
    const oldItem = oldItems.get(key);
    if (!oldItem) continue;

    // Ref change = structural
    if (oldItem.ref !== newItem.ref) {
      changedRefItems.push({ old: oldItem, new: newItem });
    } else if (
      !deepEqual(oldItem.placeholders, newItem.placeholders) ||
      !deepEqual(oldItem.materialize, newItem.materialize)
    ) {
      // Placeholder/materialize changes are structural: they alter interpolation
      // semantics and require re-prompting the user or re-running the apply pipeline.
      changedRefItems.push({ old: oldItem, new: newItem });
    } else if (!deepEqual(oldItem.config, newItem.config)) {
      // Config-only changes are non-structural (safe to auto-propagate).
      configChangedItems.push({ old: oldItem, new: newItem });
    }
  }

  // Classify
  const isStructural =
    addedItems.length > 0 || removedItems.length > 0 || changedRefItems.length > 0;

  const kind: ChangeKind = isStructural ? "structural" : "non-structural";

  const summaryParts: string[] = [];
  if (addedItems.length > 0) summaryParts.push(`+${addedItems.length} items`);
  if (removedItems.length > 0) summaryParts.push(`-${removedItems.length} items`);
  if (changedRefItems.length > 0) summaryParts.push(`${changedRefItems.length} ref changes`);
  if (configChangedItems.length > 0)
    summaryParts.push(`${configChangedItems.length} config changes`);

  const summary = summaryParts.length > 0 ? `${kind}: ${summaryParts.join(", ")}` : "no changes";

  return {
    kind,
    addedItems,
    removedItems,
    changedRefItems,
    configChangedItems,
    summary,
  };
}

/**
 * Determine if a preset upgrade can proceed automatically or needs user confirmation.
 *
 * @param oldManifest - Currently applied version.
 * @param newManifest - Target version.
 * @returns Upgrade result indicating auto-upgradability and required actions.
 * @docLink packages/library/concepts#upgrade-strategies
 */
export function evaluateUpgrade(
  oldManifest: PresetManifest,
  newManifest: PresetManifest,
): UpgradeResult {
  const diff = detectChanges(oldManifest, newManifest);

  if (diff.kind === "non-structural") {
    return { autoUpgradable: true, diff };
  }

  // Structural changes require explicit approval
  const requiredActions: string[] = [];

  if (diff.addedItems.length > 0) {
    requiredActions.push(
      `Create ${diff.addedItems.length} new instance(s): ${diff.addedItems.map((i) => i.ref).join(", ")}`,
    );
  }

  if (diff.removedItems.length > 0) {
    requiredActions.push(
      `Remove ${diff.removedItems.length} instance(s): ${diff.removedItems.map((i) => i.ref).join(", ")}`,
    );
  }

  if (diff.changedRefItems.length > 0) {
    requiredActions.push(`Replace ${diff.changedRefItems.length} instance(s) with new refs`);
  }

  return { autoUpgradable: false, diff, requiredActions };
}

/**
 * Apply a non-structural upgrade to existing instances.
 *
 * Updates config on existing instances to match the new preset version's
 * config/materialize/placeholder defaults. Only runs when `evaluateUpgrade`
 * returns `autoUpgradable: true`.
 *
 * @param newManifest - The new preset manifest.
 * @param existingInstances - Map of item key to instance ID.
 * @param library - Library instance used to call `updateInstance`.
 * @returns Count of updated instances and any per-instance errors.
 * @docLink packages/library/concepts#upgrade-strategies
 */
export async function applyNonStructuralUpgrade(
  newManifest: PresetManifest,
  existingInstances: Map<string, string>,
  library: {
    updateInstance: (id: string, patch: { config?: Record<string, unknown> }) => Promise<unknown>;
  },
): Promise<{ updated: number; errors: string[] }> {
  let updated = 0;
  const errors: string[] = [];

  for (const item of newManifest.items) {
    const key = itemKey(item);
    const instanceId = existingInstances.get(key);
    if (!instanceId) continue;

    if (item.config) {
      try {
        await library.updateInstance(instanceId, {
          config: item.config as Record<string, unknown>,
        });
        updated++;
      } catch (err) {
        errors.push(
          `Failed to update instance "${instanceId}": ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  }

  return { updated, errors };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Create a stable key for matching items across versions.
 * Uses `id` if available, falls back to `ref`.
 */
function itemKey(item: PresetItem): string {
  return item.id ?? item.ref;
}

/**
 * Index items by their stable key.
 */
function indexItems(items: PresetItem[]): Map<string, PresetItem> {
  const map = new Map<string, PresetItem>();
  for (const item of items) {
    map.set(itemKey(item), item);
  }
  return map;
}

/**
 * Deep equality check (JSON-based, suitable for config objects).
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined && b === undefined) return true;
  if (a === null && b === null) return true;
  if (a === undefined || b === undefined) return false;
  if (a === null || b === null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
